import {
  ChevronDown,
  ChevronUp,
  Settings,
  Info,
  Lightbulb,
  X,
  ArrowRight,
  Maximize2,
  Minimize2,
} from "lucide-react";
import React, {
  ReactChild,
  useRef,
  ReactNode,
  memo,
  useState,
} from "react";
import Icon from "./icons";

interface IProps {
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  count?: number;
  active?: boolean;
  cursor?: string;
  icon?: ReactChild;
  padding?: string;
  className?: string;
  open?: boolean;
  hoverable?: boolean;
  onClick?: () => void;
}

export const SectionHeader = ({
  children,
  title,
  subtitle,
  count,
  icon,
}: IProps) => {
  return (
    <div className="mb-4 mt-4">
      <h1 className="text-primary text-2xl">
        {icon && <>{icon}</>}
        {title}
        {count !== null && (
          <span className="text-accent mr-1 ml-2 text-xs">{count}</span>
        )}
      </h1>
      {subtitle && <span className="inline-block">{subtitle}</span>}
      {children}
    </div>
  );
};

export const SectionTitle = ({
  children,
  title,
  subtitle,
  count,
  icon,
  className,
}: IProps) => {
  return (
    <div className="mb-4">
      <h1 className={`${className} text-4xl font-semibold`}>
        {icon && <>{icon}</>}
        {title}
        {count !== null && (
          <span className="text-accent mr-1 ml-2 text-xs">{count}</span>
        )}
      </h1>
      {subtitle && <span className="inline-block">{subtitle}</span>}
      {children}
    </div>
  );
};

export const Highlight = ({ children }: IProps) => {
  return <span className=" border-accent border-b ">{children}</span>;
};

export const IconButton = ({
  onClick,
  icon,
  className,
  active = false,
}: IProps) => {
  return (
    <span
      role={"button"}
      onClick={onClick}
      className={`inline-block mr-2 hover:text-accent transition duration-300 ${className} ${
        active ? "border-accent border rounded text-accent" : ""
      }`}
    >
      {icon}
    </span>
  );
};

export const LaunchButton = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => {
  return (
    <button
      role={"button"}
      className={`    focus:ring ring-accent  ring-l-none  p-3 px-5 rounded  cursor-pointer hover:brightness-110 bg-accent transition duration-500    text-white`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const SecondaryButton = ({ children, onClick, className }: { children: React.ReactNode; onClick?: () => void; className?: string }) => {
  return (
    <button
      role={"button"}
      className={` ${className} inline-block  focus:ring ring-accent  p-3 px-5 rounded  cursor-pointer hover:brightness-110 bg-secondary transition duration-500    text-primary`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const Card = ({
  children,
  title,
  subtitle,
  hoverable = true,
  active,
  cursor = "cursor-pointer",
  className = "p-3",
  onClick,
}: IProps) => {
  let border = active
    ? "border-accent"
    : "border-secondary hover:border-accent ";
  border = hoverable ? border : "border-secondary";

  return (
    <div
      onClick={onClick}
      role={"button"}
      className={`${border} border-2 bg-secondary  group ${className} rounded ${cursor} transition duration-300`}
    >
      <div className="mt- text-sm text-secondary  break-words">
        {title && (
          <div className="text-accent rounded font-semibold  text-xs pb-1">
            {title}
          </div>
        )}
        <div>{subtitle}</div>
      </div>
    </div>
  );
};

export const CollapseBox = ({
  title,
  children,
  className = " p-3",
  open = false,
}: IProps) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(open);
  const chevronClass = "h-4 cursor-pointer inline-block mr-1";
  return (
    <div
      onMouseDown={(e) => {
        if (e.detail > 1) {
          e.preventDefault();
        }
      }}
      className="border border-gray-200 rounded"
    >
      <div
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="cursor-pointer bg-gray-100 p-2 rounded-t"
      >
        {isOpen && <ChevronUp className={chevronClass} />}
        {!isOpen && <ChevronDown className={chevronClass} />}

        <span className=" inline-block -mt-2 mb-2 text-xs">
          {" "}
          {title}
        </span>
      </div>

      {isOpen && (
        <div className={`${className} bg-gray-50  rounded`}>
          {children}
        </div>
      )}
    </div>
  );
};

export const LoadBox = ({
  subtitle,
  className = "my-2 text-accent ",
}: IProps) => {
  return (
    <div className={`${className} `}>
      {" "}
      <span className="mr-2 ">
        {" "}
        <Icon size={5} icon="loading" />
      </span>{" "}
      {subtitle}
    </div>
  );
};

export const LoadingBar = ({ children }: IProps) => {
  return (
    <>
      <div className="rounded bg-secondary mt-4 p-3">
        <span className="inline-block h-6 w-6 relative mr-2">
          <Settings className="animate-ping text-accent absolute inline-flex h-full w-full rounded-ful  opacity-75" />
          <Settings className="relative text-accent animate-spin  inline-flex rounded-full h-6 w-6" />
        </span>
        {children}
      </div>
      <div className="relative">
        <div className="loadbar rounded-b"></div>
      </div>
    </>
  );
};

export const MessageBox = ({ title, children, className }: IProps) => {
  const messageBox = useRef<HTMLDivElement>(null);

  const closeMessage = () => {
    if (messageBox.current) {
      messageBox.current.remove();
    }
  };

  return (
    <div
      ref={messageBox}
      className={`${className} p-3  rounded  bg-secondary transition duration-1000 ease-in-out  overflow-hidden`}
    >
      {" "}
      <div className="flex gap-2 mb-2">
        <div className="flex-1">
          <span className="font-semibold text-primary text-base">{title}</span>
        </div>
        <div>
          <span
            onClick={() => {
              closeMessage();
            }}
            className=" border border-secondary bg-secondary brightness-125 hover:brightness-100 cursor-pointer transition duration-200   inline-block px-1 pb-1 rounded text-primary"
          >
            <X className="h-4 w-4 inline-block" />
          </span>
        </div>
      </div>
      {children}
    </div>
  );
};

export const ExtLink = ({ children, link, title, newTab, content }: { children?: React.ReactNode; link?: string; title?: string; newTab?: boolean; content?: React.ReactNode }) => {
  if (newTab) {
    return (
      <a
        title={title}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        bingo {content}
      </a>
    );
  } else {
    return (
      <a
        href={link}
        className="font-medium text-accent hover:text-accent hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }
};

export const Callout = ({
  children,
  title = null,
}: {
  children: ReactNode;
  title?: string | null;
}) => {
  return (
    <aside className="relative bg-secondary border-l-2 mt-4 border-accent pl-5 pr-3 py-2 pb-2 rounded-r text-base">
      <span className="-ml-10 mt-5 absolute  text-accent ">
        <div
          style={{ paddingTop: "3px" }}
          className="  rounded-full h-10 w-10 pl-2  bg-primary   border-accent border-2  "
        >
          <Info className="w-5 h-5 inline-block" />
        </div>
      </span>
      <div className="ml-2">
        {title && <div className="font-semibold mt-1">{title}</div>}
        {children}
      </div>
    </aside>
  );
};

export const CalloutLink = ({
  title = "",
  link = null,
  description = null,
  icon = null,
  children = null,
}: {
  title?: string;
  link?: string | null;
  description?: string | null;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  return (
    <div className="rounded p-2 mb-2 px-3 bg-secondary">
      <div className="">
        <>
          {icon && <span className="text-accent">{icon} </span>}
          {link && <ExtLink link={link}>{title}</ExtLink>}
          {!link && <span> {title} </span>}
        </>
      </div>
      <div className="text-sm mt-1 text-normal">{description}</div>
      {children && <div>{children}</div>}
      <span className="block mb-1 pr-2 exa no-underline   text-right">
        <ExtLink link={link || undefined}>
          {" "}
          more <ArrowRight className="inline-block h-4 w-4" />
        </ExtLink>
      </span>
    </div>
  );
};

export const Spacer = () => {
  return (
    <span style={{ fontSize: "1px" }} className="opacity-0 -ml-4 border w-1">
      p
    </span>
  );
};

export const CalloutInfo = ({
  title = "",
  link = null,
  icon = <Lightbulb className="w-6 h-6 text-accent inline-block" />,
  children = null,
}: {
  title?: string;
  link?: string | null;
  description?: string | null;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  return (
    <div
      style={{ minHeight: "40px" }}
      className="rounded border-l-2 border-accent mt-4 relative p-2 mb-4 px-3 bg-secondary"
    >
      <div className="absolute -ml-8 bg-secondary p-2  border-accent rounded-full w-10 h-10">
        {icon}
      </div>
      {children && (
        <div className="ml-2 pb-2">
          <div className="">
            {link && <a href={link}>{title}</a>}
            {!link && (
              <span className="py-2 inline-block font-semibold"> {title} </span>
            )}
          </div>

          {children}
        </div>
      )}
    </div>
  );
};

export const TruncatableText = memo(
  ({
    content,
    isJson = false,
    className = "",
    jsonThreshold = 1000,
    textThreshold = 500,
  }: {
    content: string;
    isJson?: boolean;
    className?: string;
    jsonThreshold?: number;
    textThreshold?: number;
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const threshold = isJson ? jsonThreshold : textThreshold;
    const shouldTruncate = content.length > threshold;

    const toggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    const displayContent =
      shouldTruncate && !isExpanded
        ? content.slice(0, threshold) + "..."
        : content;

    return (
      <div className="relative">
        <div
          className={`
            transition-[max-height,opacity] duration-500 ease-in-out
            ${
              shouldTruncate && !isExpanded
                ? "max-h-[300px]"
                : "max-h-[10000px]"
            }
            ${className}
          `}
        >
          {displayContent}
          {shouldTruncate && !isExpanded && (
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-secondary/20 to-transparent" />
          )}
        </div>

        {shouldTruncate && (
          <div className="mt-2 flex items-center justify-end">
            <button
              type="button"
              onClick={toggleExpand}
              className={`
                inline-flex items-center gap-2 px-3 py-1.5
                rounded bg-secondary/80
                text-xs font-medium
                transition-all duration-300
                 hover:text-accent
                hover:scale-105
                z-10
              `}
              aria-label={isExpanded ? "less" : "more"}
            >
              <span>{isExpanded ? "Show less" : "Show more"}</span>
              {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        )}
      </div>
    );
  }
);
